import type { AnatomyBinding, AnatomySize } from "./componentAnatomy";
import { anatomyTextStyle } from "./componentAnatomyText";

/**
 * @purpose Button's size axis, mined from the five `@include size(...)` calls in
 * `Button.module.scss` (extraction §2). Every size re-declares the same four
 * things — height, padding, gap and text style — and nothing else, which is why
 * the colour layer lives with the variants instead of being duplicated here.
 *
 * Radius is the exception: the base block sets `--radius-m` and only `.xs` moves
 * off it, so only `.xs` carries a radius binding.
 */

const geometry = ({
    height,
    paddingX,
    gap,
}: {
    height: string;
    paddingX: `--${string}`;
    gap: `--${string}`;
}): readonly AnatomyBinding[] => [
    {
        partId: "background",
        property: "height",
        literal: height,
        note: "A dimension rather than a spacing step, so the sheet declares it outright.",
    },
    {
        partId: "background",
        property: "padding-inline",
        token: paddingX,
        via: "--button-padding-x",
    },
    { partId: "background", property: "gap", token: gap, via: "--button-gap" },
];

export const buttonSizes: readonly AnatomySize[] = [
    {
        id: "xs",
        label: "xs",
        selector: ".xs",
        bindings: [
            ...geometry({
                height: "20px",
                paddingX: "--space-6",
                gap: "--space-4",
            }),
            {
                partId: "border",
                property: "border-radius",
                token: "--radius-s",
                via: "--button-radius",
                note: "20px is the only size off --radius-m, and only outside the link variant.",
            },
            ...anatomyTextStyle({
                scale: "xs",
                weight: "500",
                mixin: "text-xs-12-medium",
            }),
        ],
    },
    {
        id: "sm",
        label: "sm",
        selector: ".sm",
        bindings: [
            ...geometry({
                height: "24px",
                paddingX: "--space-8",
                gap: "--space-4",
            }),
            ...anatomyTextStyle({
                scale: "xs",
                weight: "500",
                mixin: "text-xs-12-medium",
            }),
        ],
    },
    {
        id: "md",
        label: "md",
        selector: ".md",
        bindings: [
            ...geometry({
                height: "28px",
                paddingX: "--space-8",
                gap: "--space-4",
            }),
            ...anatomyTextStyle({
                scale: "s",
                weight: "500",
                mixin: "text-s-14-medium",
            }),
        ],
    },
    {
        id: "lg",
        label: "lg",
        selector: ".lg",
        bindings: [
            ...geometry({
                height: "32px",
                paddingX: "--space-12",
                gap: "--space-8",
            }),
            ...anatomyTextStyle({
                scale: "s",
                weight: "500",
                mixin: "text-s-14-medium",
            }),
        ],
    },
    {
        id: "xl",
        label: "xl",
        selector: ".xl",
        bindings: [
            ...geometry({
                height: "40px",
                paddingX: "--space-16",
                gap: "--space-8",
            }),
            ...anatomyTextStyle({
                scale: "s",
                weight: "500",
                mixin: "text-s-14-medium",
            }),
        ],
    },
];
